import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PenLine, Eraser, Check, Loader2 } from "lucide-react";

/**
 * HTML5 canvas signature pad. Captures touch + mouse strokes, converts to PNG base64,
 * and saves to /api/rentals/:id/signature.
 *
 * Props:
 *   rentalId: string
 *   existing: optional base64 string (data: URL or raw base64) — if provided, displays it
 *   onSaved: callback after save
 */
export default function SignaturePad({ rentalId, existing, existingInitials, onSaved, endpoint, savedMessage, showInitials = true }) {
  const { t } = useT();
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(!existing);
  const [saving, setSaving] = useState(false);
  const [initials, setInitials] = useState((existingInitials || "").toUpperCase());

  // High-DPI sizing
  const resize = () => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A0A0A";
    // Restore existing signature if any
    if (existing) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        setIsEmpty(false);
      };
      img.src = existing.startsWith("data:") ? existing : `data:image/png;base64,${existing}`;
    }
  };

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(e);
    setIsEmpty(false);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  };

  const end = () => {
    drawingRef.current = false;
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setIsEmpty(true);
  };

  const save = async () => {
    if (isEmpty) {
      toast.error(t("signature.emptyError"));
      return;
    }
    setSaving(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const url = endpoint || `/rentals/${rentalId}/signature`;
      const body = { signature_b64: dataUrl };
      if (showInitials && initials.trim()) {
        body.initials_text = initials.trim().toUpperCase().slice(0, 6);
      }
      const { data } = await api.post(url, body);
      if (savedMessage) {
        toast.success(savedMessage);
      } else if (data?.contract_stored) {
        toast.success(t("signature.savedAndStored"));
      } else if (data?.warning) {
        toast.warning(data.warning);
      } else {
        toast.success(t("signature.saved"));
      }
      onSaved?.(dataUrl, data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-zinc-200 bg-white" data-testid={`signature-pad-${rentalId}`}>
      <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PenLine className="w-4 h-4 text-[#F5A623]" />
          <div className="overline">{t("signature.title")}</div>
        </div>
        <div className="text-[10px] text-zinc-500 font-mono uppercase">{t("signature.hint")}</div>
      </div>
      <div className="p-4 space-y-3">
        {showInitials && (
          <div className="bg-orange-50 border border-orange-200 px-3 py-2.5 flex items-center gap-3" data-testid="initials-row">
            <div className="flex-1">
              <div className="overline text-[10px] mb-0.5">{t("signature.initialsLabel")}</div>
              <p className="text-[11px] text-zinc-600 leading-tight">{t("signature.initialsHint")}</p>
            </div>
            <input
              type="text"
              value={initials}
              onChange={(e) => setInitials(e.target.value.toUpperCase().slice(0, 6))}
              placeholder="JD"
              maxLength={6}
              className="border border-zinc-300 bg-white rounded-none px-3 py-2 w-24 text-center font-mono font-bold text-lg tracking-widest uppercase focus:outline-none focus:border-[#F5A623]"
              data-testid="initials-input"
            />
          </div>
        )}
        <div className="relative border border-dashed border-zinc-300 bg-[radial-gradient(circle,#E4E4E7_1px,transparent_1px)] [background-size:16px_16px]">
          <canvas
            ref={canvasRef}
            className="w-full h-48 touch-none cursor-crosshair block"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
            data-testid="signature-canvas"
          />
          {isEmpty && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-zinc-400 text-xs font-mono uppercase tracking-wider">{t("signature.placeholder")}</div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 pt-3">
          <Button
            type="button"
            onClick={clear}
            variant="outline"
            className="rounded-none"
            disabled={isEmpty || saving}
            data-testid="signature-clear"
          >
            <Eraser className="w-4 h-4 mr-2" /> {t("signature.clear")}
          </Button>
          <Button
            type="button"
            onClick={save}
            className="rounded-none bg-[#F5A623] hover:bg-[#D97706] text-white"
            disabled={saving || isEmpty}
            data-testid="signature-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            {t("signature.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
